import {
  Breadcrumb,
  Button,
  Col,
  Empty,
  Form,
  Radio,
  Row,
  Space,
  notification,
  Typography,
} from 'antd';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { generatePath, Link, useNavigate, useParams } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { shouldAskAdditionalInfo } from '@/shared/lib/shouldAskAdditionalData';
import { Layout } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { billingModel, EffectToAddType } from '@/entities/billing';
import { organizationModel } from '@/entities/organization';
import { organizationsModel } from '@/entities/organizations';

import { AdditionalDataForm } from '@/features/additionalDataForm';
import {
  BillingProductAccessGuard,
  useBillingProductAccessGuard,
} from '@/features/billingProductAccessGuard';
import {
  CUSTOM_PRICE_FIELD_NAME,
  CustomPriceField,
  getInvoicePrice,
  normalizeCustomPrice,
} from '@/features/customPrice';

import styles from './AddDiagnocoins.module.scss';

const { Title, Text } = Typography;

type AddDiagnocoinsFormType = {
  packSKU?: string;
  customPrice?: number | null;
};

const selectNewOrderLoadingState =
  billingModel.selectors.getLoadingStateSelector('newOrder');

export const AddDiagnocoins: FC = () => {
  const [isAdditionalFormOpen, setIsAdditionalFormOpen] =
    useState<boolean>(false);

  const { organizationID } = useParams<{ organizationID: string }>();

  const navigate = useNavigate();

  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const listOrganization = useAppSelector(
    organizationsModel.selectors.selectById(organizationID),
  );
  const canEditInventoryProducts = useAppSelector(
    authModel.selectors.selectCanEditInventoryProducts,
  );
  const allPacks = useAppSelector(
    billingModel.selectors.selectAvailableDiagnocoinPacks,
  );
  // The only coin packs that exist today are dev-only and admin-only, so an
  // operator without that permission sees nothing rather than a broken order.
  const packs = canEditInventoryProducts
    ? allPacks
    : allPacks.filter((product) => !product.AvailableForAdminsOnly);
  const newOrderLoadingState = useAppSelector(selectNewOrderLoadingState);

  const {
    ID: accountID,
    BillingZones,
    BillingInformation,
  } = useAppSelector(billingModel.selectors.selectAccountInfo);

  const BillingZone = BillingZones?.at(0);

  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();
  const billingProductAccessState = useBillingProductAccessGuard({
    accessDeniedDescription: formatMessage({
      id: 'addDiagnocoinsPage.accessDenied.message',
      defaultMessage:
        'You do not have permission to add Diagnocoins for this organization.',
    }),
    organizationID,
  });
  const [form] = Form.useForm<AddDiagnocoinsFormType>();

  const selectedPackSKU = Form.useWatch('packSKU', form);
  const selectedPack = packs.find(({ SKU }) => SKU === selectedPackSKU);

  const onFinish = async (data: AddDiagnocoinsFormType) => {
    const targetPack = packs?.find((pack) => pack.SKU === data.packSKU);
    const customPrice = normalizeCustomPrice(data[CUSTOM_PRICE_FIELD_NAME]);

    // Coins are credited by processing a paid order, never minted directly, so
    // every batch keeps an OrderID linking it back to the money that bought it.
    const effectToAdd: EffectToAddType = {
      case: 'DiagnocoinPurchase',
      value: {
        Product: targetPack,
      },
    };

    if (
      shouldAskAdditionalInfo(BillingZone, BillingInformation) &&
      getInvoicePrice(targetPack?.Price, customPrice) > 0
    ) {
      dispatch(
        billingModel.actions.setOrderToAdd({
          Effect: effectToAdd,
          CustomPrice: customPrice,
        }),
      );

      setIsAdditionalFormOpen(true);

      return;
    }

    try {
      dispatch(
        billingModel.actions.setLoading({
          loaderKey: 'newOrder',
          loaderState: 'pending',
        }),
      );

      await dispatch(
        billingModel.thunks.createOrder({
          AccountID: accountID,
          Effect: effectToAdd,
          CustomPrice: customPrice,
        }),
      ).unwrap();

      navigate(generatePath(PATHS.organization, { organizationID }));
    } catch (error) {
      notification.error({
        message:
          error instanceof Error
            ? error.message
            : formatMessage({
                id: 'global.network.error',
                defaultMessage: 'Network error',
              }),
      });

      dispatch(
        billingModel.actions.setLoading({
          loaderKey: 'newOrder',
          loaderState: 'failed',
        }),
      );
    } finally {
      dispatch(
        billingModel.actions.setLoading({
          loaderKey: 'newOrder',
          loaderState: 'idle',
        }),
      );
    }
  };

  useEffect(() => {
    const getProducts = async () => {
      const { AvailableProducts } = await dispatch(
        billingModel.thunks.getAvailableProducts({
          Condition: { case: 'ByAccountID', value: accountID },
        }),
      ).unwrap();

      dispatch(billingModel.actions.setAvailableProducts(AvailableProducts));
    };

    if (accountID) {
      getProducts();
    }
  }, [accountID]);

  if (billingProductAccessState !== 'allowed') {
    return <BillingProductAccessGuard state={billingProductAccessState} />;
  }

  return (
    <Layout.Content>
      <Breadcrumb
        className="p2"
        style={{ padding: '24px 0' }}
        items={[
          {
            title: (
              <Link className={styles.breadcrumb} to={generatePath(PATHS.main)}>
                <FormattedMessage id="breadcrumb.home" defaultMessage="Home" />
              </Link>
            ),
          },
          {
            title: (
              <Link
                className={styles.breadcrumb}
                to={generatePath(PATHS.organization, { organizationID })}
              >
                {organization?.Name ?? listOrganization?.Name ?? ''}
              </Link>
            ),
          },
          {
            title: (
              <FormattedMessage
                id="addDiagnocoinsPage.title"
                defaultMessage="Add Diagnocoins"
              />
            ),
          },
        ]}
      />

      <Form
        form={form}
        name="add-diagnocoins"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row gutter={32}>
          <Col span={12}>
            <Title level={4}>
              <FormattedMessage
                id="addDiagnocoinsPage.stepOne"
                defaultMessage="Select coin pack"
              />
            </Title>

            <Form.Item
              name="packSKU"
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'forms.required',
                    defaultMessage: 'Field is required',
                  }),
                },
              ]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  {packs.map(({ SKU, Name, Price, Currency, Kind }) => (
                    <Radio key={SKU} value={SKU}>
                      <Title style={{ marginBottom: 0 }} level={5}>
                        <>
                          <Text>{Name}</Text>
                          {' | '}
                          <FormattedNumber
                            minimumFractionDigits={0}
                            maximumFractionDigits={2}
                            value={Number(Price ?? '0')}
                            // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currencyDisplay="narrowSymbol"
                            currency={Currency}
                          />
                          {' | '}
                          {Kind.case === 'Diagnocoin' &&
                            formatMessage(
                              {
                                id: 'addDiagnocoinsPage.coinAmount',
                                defaultMessage:
                                  '{coins, plural, one {# coin} other {# coins}}',
                              },
                              { coins: Number(Kind.value.CoinAmount ?? 0) },
                            )}
                        </>
                      </Title>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>

            <CustomPriceField
              listPrice={selectedPack?.Price}
              currency={selectedPack?.Currency}
            />
          </Col>
        </Row>

        {packs.length > 0 ? (
          <Button
            type="primary"
            htmlType="submit"
            loading={newOrderLoadingState === 'pending'}
          >
            <span>
              <FormattedMessage
                id="addDiagnocoinsPage.submit"
                defaultMessage="Add Diagnocoins"
              />
            </span>
          </Button>
        ) : (
          <Empty
            description={
              <FormattedMessage
                id="addDiagnocoinsPage.noPacks"
                defaultMessage="No Diagnocoin packs available for this billing zone"
              />
            }
          />
        )}
      </Form>

      <AdditionalDataForm
        isOpen={isAdditionalFormOpen}
        onCloseHandler={() => setIsAdditionalFormOpen(false)}
      />
    </Layout.Content>
  );
};
